import React, { Suspense, useContext, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { IntlProvider } from './context/IntlContext';
import { AppGlobalContext, GlobalContext } from './context/GlobalContext';
import { HelpCenter } from './components/widgets/HelpCenter';
import { ErrorBoundary } from './components/structure/ErrorBoundary';
import { Header } from './components/structure/Header';
import { NotificationProvider } from './components/structure/Notification';
import { hasPermission, UserPermission } from './services/rights';
import { ShepherdJourneyProvider } from 'react-shepherd';
import { SgwtMiniFooter } from '@sgwt/sgwt-widgets-react';
import './App.scss';

const PageNotFound = React.lazy(() => import('./components/structure/PageNotFound'));
const ExplorePage = React.lazy(() => import('./routes/explore/ExplorePage'));
const PlatformsPage = React.lazy(() => import('./routes/explore/platform/PlatformsPage'));
const Project = React.lazy(() => import('./routes/project/Project'));
const ProjectCreation = React.lazy(() => import('./routes/project/edition/ProjectCreation'));
const CertificationPage = React.lazy(() => import('./routes/certification/CertificationPage'));
const AdministrationPage = React.lazy(() => import('./routes/administration/AdministrationPage'));
const MonitoringPage = React.lazy(() => import('./routes/monitoring/MonitoringPage'));
const DocumentationPage = React.lazy(() => import('./routes/documentation/DocumentationPage'));
const DownloadPage = React.lazy(() => import('./routes/download/DownloadPage'));

const RequireAdministrationRights = ({ children }: { children: JSX.Element }) => {
  const admin = hasPermission(UserPermission.ADMINISTRATION_PERMISSION);
  if (!admin) {
    return <Navigate to="/" replace />;
  }
  return children;
};

const Skeleton = () => (
  <div className="explore-page-content">
    <div className="container">
      <div className="home-page-banner position-relative">
        <div className="explore-page-header">
          <div className="display-2 pt-4 mt-5 pb-5">
            <div className="loading-block w-75 mb-3" style={{ height: '50px' }} />
            <div className="loading-block w-100 mb-3" style={{ height: '50px' }} />
          </div>
        </div>
      </div>
    </div>

    <div className="p-5 bg-lvl2" style={{ minHeight: '68vh' }}>
      <div className="container">
        {[100, 75, 50, 100, 75, 100, 50, 75].map((width: number, index: number) => (
          <div key={index} className={`loading-block mb-3 w-${width}`} style={{ height: '50px' }} />
        ))}
      </div>
    </div>
  </div>
);

const AppContent = () => {
  const { isInIframe } = useContext(GlobalContext);

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
      },
    },
  });

  return (
    <>
      <Header />
      <div className="main-content">
        <Suspense fallback={<Skeleton />}>
          <QueryClientProvider client={queryClient}>
            <Routes>
              <Route path="/" element={<Navigate to="/explore/all" />} />
              <Route path="/explore/platforms" element={<PlatformsPage />} />
              <Route path="/explore/:origin" element={<ExplorePage />} />
              <Route path="/explore/:origin/:projectId/*" element={<Project />} />
              <Route path="/add/euc" element={<ProjectCreation />} />
              <Route path="/add/project" element={<ProjectCreation />} />
              <Route path="/certification/" element={<Navigate to="/certification/owner/?campaign=true" />} />
              <Route path="/certification/:view" element={<CertificationPage />} />
              <Route path="/monitoring/*" element={<MonitoringPage />} />
              <Route path="/documentation" element={<DocumentationPage />} />
              <Route path="/download" element={<DownloadPage />} />

              <Route
                path="/administration"
                element={
                  <RequireAdministrationRights>
                    <AdministrationPage />
                  </RequireAdministrationRights>
                }
              />

              <Route path="*" element={<PageNotFound />} />
            </Routes>
          </QueryClientProvider>
        </Suspense>
      </div>
      {!isInIframe && (
        <>
          <HelpCenter />
          <SgwtMiniFooter />
        </>
      )}
    </>
  );
};

const App = () => {
  const context: AppGlobalContext = useMemo(
    () => ({
      isInIframe: window.self !== window.top,
    }),
    []
  );

  return (
    <ErrorBoundary>
      <IntlProvider>
        <GlobalContext.Provider value={context}>
          <NotificationProvider>
            <BrowserRouter>
              {context.isInIframe ? (
                <AppContent />
              ) : (
                <ShepherdJourneyProvider>
                  <AppContent />
                </ShepherdJourneyProvider>
              )}
            </BrowserRouter>
          </NotificationProvider>
        </GlobalContext.Provider>
      </IntlProvider>
    </ErrorBoundary>
  );
};

export default App;
